/**
 * ADR-0048 — the "Promote to production" section on a catalogue item, in the
 * TRAINING planner only.
 *
 * Ed, 2026-09-18, choosing where it lives: on each item, when you open it —
 * "one item, one deliberate act". So this renders as a section at the foot of
 * the item's detail panel: what production has of this item today, and one
 * button.
 *
 * Everything this file hides, the server also refuses
 * (functions/lib/catalogue_promotion.py). Hiding is courtesy; the refusal is
 * the rule. Refusal text is shown verbatim, because the server writes it for
 * the person who pressed the button — "Promote its parent first — production
 * has no goalCategory spinal."
 */
import { escapeHtml } from "../utils/html";
import { firestoreDatabaseId } from "../firebase";
import { state } from "../state";
import { callCataloguePromote } from "../admin/cf-client";
import {
  canPromote,
  promoteButtonLabel,
  promotionStatusText,
} from "./promote-rules";

interface ItemProvenance {
  origin?: string;
  promotedAt?: string;
  promotionCount?: number;
}

/**
 * Render the section into `container` (the item's detail panel), for the item
 * at `collection/docId`. Renders nothing at all where promotion isn't on
 * offer — no disabled button, no explanation: on a production build there is
 * nothing to promote TO, and the section would be noise on every item.
 */
export function renderPromoteSection(
  container: HTMLElement,
  collection: string,
  docId: string,
  provenance: ItemProvenance | null | undefined,
): void {
  if (!canPromote(firestoreDatabaseId, state.authClaims?.role)) return;
  if (!docId) return;

  const section = document.createElement("div");
  section.className = "detail-section catalogue-promote";
  section.innerHTML = `
    <h4>Production</h4>
    <div class="promote-status" id="promoteStatus" aria-live="polite">${escapeHtml(promotionStatusText(provenance))}</div>
    <button class="btn-outline" id="promoteToProduction"
            title="Copy this item into production's catalogue, keeping its id, and record who promoted it">
      ${escapeHtml(promoteButtonLabel(provenance))}
    </button>
  `;
  container.appendChild(section);

  const status = section.querySelector<HTMLElement>("#promoteStatus")!;
  const button = section.querySelector<HTMLButtonElement>("#promoteToProduction")!;

  button.addEventListener("click", () => {
    void (async () => {
      button.disabled = true;
      status.textContent = "Promoting…";
      status.classList.remove("promote-status-error");
      try {
        const result = await callCataloguePromote({ collection, docId });
        status.textContent = result.kind === "update"
          ? "Updated in production."
          : "Now in production.";
        button.textContent = "Promote again";
      } catch (err) {
        // Verbatim: the server's refusals name what to do next.
        status.textContent = err instanceof Error ? err.message : "Could not promote.";
        status.classList.add("promote-status-error");
      } finally {
        button.disabled = false;
      }
    })();
  });
}
